import React, { useState, useMemo, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import map from 'lodash/map';
import join from 'lodash/join';
import moment from 'moment';

import LostPointsOfSale from 'components/LostPointsOfSale';
import Pills from 'components/Pills';
import Pellet from 'components/Pellet';
import Card from 'components/Card/';
import CardHeader from 'components/CardHeader';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import ExportButton from 'components/Export';
import ColumnVisibilityModal from 'components/ColumnVisibilityModal';
import PointOfSalesTable from '../components/PointOfSales';
import PosRecapTable from '../components/PosRecap';
import {
  PointOfSalesContainer,
  Split,
  PillPos
} from './PointsOfSales/container';

import { useDistributionPos } from 'apiTyped/hooks/distribution.query';
import { formatParam } from 'utils/formatParam';

const PointsOfSales = ({ form, isBranded, clientTags }) => {
  // Load activePill from localStorage if available
  const getInitialActivePill = () => {
    try {
      const saved = localStorage.getItem('pos_active_pill');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that it's a valid pill value
        if (['all', 'pos', 'lost', 'todo', 'other'].includes(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error loading active pill from localStorage:', e);
    }
    return 'all';
  };

  const isLatestCampaign =
    form.currentCampaign &&
    (form.currentCampaign.year === 9999 ||
      form.currentCampaign.continuous_mode);

  const [state, setState] = useState({
    showAll: false,
    hiddenColumns: [],
    activePill: getInitialActivePill(),
    sortTable: [{ id: 'client_refs_by_wine', desc: true }]
  });
  const isContinuousMode =
    form.currentCampaign &&
    form.currentCampaign.continuous_mode &&
    form.currentCampaign.market.human_validation === true;

  // Memoize the query options to prevent unnecessary re-fetches
  const queryOptions = useMemo(() => {
    if (!form.wine?.length) return {};

    return isBranded
      ? {
          brands: form.wine.map((item) => item.label),
          wine_type: form.wineType
        }
      : {
          wines: form.wine.map((item) => item.label),
          wine_type: form.wineType
        };
  }, [form.wine, isBranded, form.wineType]);

  // Use the new hook to fetch POS data
  const { isLoading, data } = useDistributionPos(
    form.currentCampaign?.id,
    queryOptions,
    !!(form.currentCampaign?.id && form.wine?.length)
  );

  const { showAll, hiddenColumns, activePill, sortTable } = state;

  // Save activePill to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('pos_active_pill', JSON.stringify(activePill));
    } catch (e) {
      console.error('Error saving active pill to localStorage:', e);
    }
  }, [activePill]);

  const filterPos = (data, state) => {
    return data && data.length > 0
      ? [...data].filter((item) => item.state === state)
      : [];
  };

  const getDataRecap = () => {
    const { evol_wines = [], evol_marques = [] } = data || {};
    const { wine } = form;
    const wineLabels = wine.map((item) => item.label);
    if (isBranded) {
      const data = [...evol_marques].filter((item) =>
        wineLabels.includes(item.marque)
      );
      return data.map((marque) => {
        const newItem = { ...marque };
        newItem.wine = marque.marque;
        if (!newItem.children) {
          newItem.children = [];
        }
        evol_wines.forEach((wine) => {
          if (newItem.wines.includes(wine.wine)) {
            newItem.children.push(wine);
          }
        });
        return newItem;
      });
    }
    return evol_wines.filter((item) => wineLabels.includes(item.wine));
  };

  const getEvolutionFromState = (state) => {
    if (state === 'pos') {
      return 'positive';
    }
    if (state === 'todo') {
      return 'neutral';
    }
    if (state === 'other') {
      return 'other';
    }
    return 'negative';
  };

  const formatData = () => {
    const posData = data?.pos || [];
    const formattedData = [...posData];
    return formattedData.map((pos) => {
      let { checks_validity_date, ...newItem } = pos;
      newItem.checks_validity_date =
        checks_validity_date !== '9999-01-01'
          ? moment(checks_validity_date, 'YYYY-MM-DD')
          : null;
      newItem.quality = {
        type: newItem.network_code,
        stars: newItem.stars,
        ws_award: newItem.ws_award,
        michelin_stars: newItem.michelin_stars,
        michelin_green_star: newItem.michelin_green_star
      };
      newItem.contact = {
        email: newItem.email,
        phone: newItem.phone,
        site: newItem.site,
        address: newItem.address,
        latitude: newItem.latitude,
        longitude: newItem.longitude
      };
      newItem.pos = {
        value: newItem.name,
        evolution: getEvolutionFromState(newItem.state)
      };
      newItem.star = Number(newItem.star);
      return newItem;
    });
  };

  const handleSort = (id) => {
    let newSortTable = sortTable;
    let desc = true;
    if (newSortTable.length > 0 && newSortTable[0].id === id) {
      desc = !newSortTable[0].desc;
    }
    newSortTable = [{ id, desc }];
    setState((prev) => ({ ...prev, sortTable: newSortTable, expanded: {} }));
  };

  const getPillOptionsWithPellet = (valuePellet, label, length) => {
    return (
      <PillPos>
        <Pellet value={valuePellet} />
        <p>{`${label} (${length})`}</p>
      </PillPos>
    );
  };

  const hasAtLeastOneRef = (data = [], property) => {
    return data
      ? data
          .map((item) => item[property])
          .reduce((base, acc) => base + acc, 0) > 0
      : false;
  };

  const hasAtLeastOneRefBtg = (data = [], property) => {
    return (
      data
        .map((item) =>
          item[property]
            ? Object.values(item[property]).reduce((base, acc) => base + acc, 0)
            : 0
        )
        .reduce((base, acc) => base + acc, 0) > 0
    );
  };

  const getColumnsNames = (data) => {
    const hasBdxRefs = hasAtLeastOneRef(data, 'references_bdx');
    const hasChampRefs = hasAtLeastOneRef(data, 'references_champ');
    const hasBdxPrices = hasAtLeastOneRef(data, 'avg_bdx_price');
    const hasChampPrices = hasAtLeastOneRef(data, 'avg_champ_price');
    const hasBtgPrices = hasAtLeastOneRefBtg(data, 'client_wines_btg');
    const hasBtgRivalPrices = hasAtLeastOneRefBtg(data, 'rival_wines_btg');
    return [
      I18n.t('restaurants&caves.pos.columns.pov'),
      isContinuousMode &&
        I18n.t('restaurants&caves.pos.columns.checkValidityDate'),
      isBranded && I18n.t('restaurants&caves.pos.columns.myBrands'),
      I18n.t('restaurants&caves.pos.columns.nbSku'),
      I18n.t('restaurants&caves.pos.columns.evol'),
      I18n.t('restaurants&caves.pos.columns.nbRival'),
      I18n.t('restaurants&caves.pos.columns.city'),
      I18n.t('restaurants&caves.pos.columns.contact'),
      I18n.t('restaurants&caves.pos.columns.quality'),
      hasBtgPrices && I18n.t('restaurants&caves.pos.columns.nbRefWine'),
      hasBtgRivalPrices &&
        I18n.t('restaurants&caves.pos.columns.nbRefWineRival'),
      hasBdxRefs && I18n.t('restaurants&caves.pos.columns.nbBdx'),
      hasChampRefs && I18n.t('restaurants&caves.pos.columns.nbChamp'),
      !hasChampRefs &&
        !hasBdxRefs &&
        I18n.t('restaurants&caves.pos.columns.nbTotal'),
      hasBdxPrices && I18n.t('restaurants&caves.pos.columns.avgPriceBdx'),
      hasChampPrices && I18n.t('restaurants&caves.pos.columns.avgPriceChamp'),
      !hasChampPrices &&
        !hasBdxPrices &&
        I18n.t('restaurants&caves.pos.columns.avgPrice')
    ].filter((item) => !!item);
  };

  const { wine, competitors } = form;
  const dataRecap = getDataRecap();
  let formattedData = formatData();
  const lostPos = filterPos(formattedData, 'lost');
  const positivePos = filterPos(formattedData, 'pos');
  const neutralPos = filterPos(formattedData, 'todo');
  const otherPos = filterPos(formattedData, 'other');
  let posData = {
    all: formattedData,
    lost: lostPos,
    todo: neutralPos,
    pos: positivePos,
    other: otherPos
  };

  let filteredData = posData[activePill];

  const options = isBranded
    ? {
        brands: formatParam(JSON.stringify(wine.map((item) => item.label))),
        rivals: formatParam(JSON.stringify(competitors)),
        wine_type: form.wineType
      }
    : {
        wines: formatParam(JSON.stringify(wine.map((item) => item.label))),
        rivals: formatParam(JSON.stringify(competitors)),
        wine_type: form.wineType
      };

  const columnsNames = getColumnsNames(filteredData);

  return (
    <PointOfSalesContainer>
      <Card>
        <CardHeader
          title={I18n.t('restaurants&caves.pos.title')}
          fontSize="20px"
          alignItems="center"
          styles={`.cardHeaderRight{width:100%;margin-left:40px;}`}
          iconRight="right"
          rightComponents={
            <div className="rightComponentsHeader">
              <Pills
                options={[
                  {
                    value: 'all',
                    label: I18n.t('restaurants&caves.pos.typeOptions.all')
                  },
                  {
                    value: 'pos',
                    label: getPillOptionsWithPellet(
                      'positive',
                      I18n.t('restaurants&caves.pos.typeOptions.pos'),
                      posData.pos.length
                    )
                  },
                  {
                    value: 'lost',
                    label: getPillOptionsWithPellet(
                      'negative',
                      I18n.t('restaurants&caves.pos.typeOptions.lost'),
                      posData.lost.length
                    )
                  },
                  {
                    value: 'todo',
                    label: getPillOptionsWithPellet(
                      'neutral',
                      I18n.t('restaurants&caves.pos.typeOptions.conquer'),
                      posData.todo.length
                    )
                  },
                  {
                    value: 'other',
                    label: getPillOptionsWithPellet(
                      'other',
                      I18n.t('restaurants&caves.pos.typeOptions.other'),
                      posData.other.length
                    )
                  }
                ].map((item) => ({
                  ...item,
                  active: activePill === item.value
                }))}
                onChange={({ value }) =>
                  setState((prev) => ({ ...prev, activePill: value }))
                }
              />
              <div className="exportButtonContainer">
                <ColumnVisibilityModal
                  columnsNames={columnsNames}
                  hiddenColumns={hiddenColumns}
                  onApply={(newHiddenColumns) =>
                    setState((prev) => ({
                      ...prev,
                      hiddenColumns: newHiddenColumns
                    }))
                  }
                />
                <ExportButton
                  path={`/distribution/${get(
                    form.currentCampaign,
                    'id'
                  )}/pos/xls?`}
                  options={options}
                  name={`${get(form, 'currentCampaign.network.name')}-${get(
                    form,
                    'currentCampaign.market.name'
                  )}-${get(form, 'currentCampaign.year')}-${join(
                    map(get(form, 'wine'), 'label'),
                    '-'
                  )}-${I18n.t('restaurants&caves.pos.title')}`}
                  xls
                  newApi
                />
              </div>
            </div>
          }
        />
        <div className="tableContainer">
          <PointOfSalesTable
            isLatestCampaign={isLatestCampaign}
            hiddenColumns={hiddenColumns}
            data={filteredData}
            sortTable={sortTable}
            showAll={showAll}
            onSortTable={(id) => handleSort(id)}
            wineType={form.wineType}
            clientMarqueNames={
              wine ? wine.map((item) => item.label).sort() : []
            }
            isBranded={isBranded}
            isContinuousMode={isContinuousMode}
            tags_names={clientTags}
            rivalMarqueNames={competitors ? competitors.sort() : []}
          />
        </div>
        <div className="showAllContainer">
          {!showAll && filteredData && filteredData.length > 20 && (
            <Button
              width="200px"
              label={`${I18n.t('seeMore')} (+${
                filteredData && filteredData.length > 0
                  ? filteredData.length - 20
                  : 0
              })`}
              color="white"
              textColor="black"
              textTransform="normal"
              border="0.8px black solid"
              fontFamily="SFCompactText"
              fontSize="12px !important"
              onClick={() => setState((prev) => ({ ...prev, showAll: true }))}
            />
          )}
          {showAll && filteredData.length > 20 && (
            <Button
              width="200px"
              label={I18n.t('seeLess')}
              color="white"
              textColor="black"
              textTransform="normal"
              border="0.8px black solid"
              fontFamily="SFCompactText"
              fontSize="12px !important"
              onClick={() => setState((prev) => ({ ...prev, showAll: false }))}
            />
          )}
        </div>
      </Card>
      <Split>
        <Card id="Point-de-Vente-Récap">
          <CardHeader
            title={I18n.t('restaurants&caves.pos.recap.title')}
            fontSize="20px"
            iconRight="right"
            rightComponents={
              <ExportButton
                name={`${get(form, 'currentCampaign.network.name')}-${get(
                  form,
                  'currentCampaign.market.name'
                )}-${get(form, 'currentCampaign.year')}-${join(
                  map(get(form, 'wine'), 'label'),
                  '-'
                )}-${I18n.t('restaurants&caves.pos.recap.title')}`}
                jpg
                pdf
                targetElement="#Point-de-Vente-Récap"
              />
            }
          />
          <PosRecapTable
            data={dataRecap}
            isBranded={isBranded}
            total={get(data, 'evol_client[0]', null)}
          />
        </Card>

        <LostPointsOfSale
          data={lostPos.map((item) => ({ ...item, status: 'negative' }))}
        />
      </Split>
      <Spinner loading={isLoading} />
    </PointOfSalesContainer>
  );
};

export default PointsOfSales;
